// Wraps the business read port in result-aware application helpers.
import type { BusinessReadPort } from '../core/types';
import { fromPromise } from '@/modules/shared/core/result';

const ERR = 'business_read_failed';

export function createBusinessReadService(port: BusinessReadPort) {
  return {
    getAllFacilities: () =>
      fromPromise(port.getAllFacilities(), ERR, 'Failed to load facilities.'),
    getActiveFacilities: (params: { isSuperAdmin: boolean; facilityIds: string[] }) =>
      fromPromise(port.getActiveFacilities(params), ERR, 'Failed to load active facilities.'),
    getManagedFacilities: (params: { isSuperAdmin: boolean; facilityIds: string[] }) =>
      fromPromise(port.getManagedFacilities(params), ERR, 'Failed to load managed facilities.'),
    getAllActiveFacilities: () =>
      fromPromise(port.getAllActiveFacilities(), ERR, 'Failed to load active facilities.'),
    getAllActiveSports: () =>
      fromPromise(port.getAllActiveSports(), ERR, 'Failed to load active sports.'),
    getFacilityNamesByIds: (facilityIds: string[]) =>
      fromPromise(port.getFacilityNamesByIds(facilityIds), ERR, 'Failed to load facility names.'),
    getSportsByFacility: (facilityId: string) =>
      fromPromise(port.getSportsByFacility(facilityId), ERR, 'Failed to load facility sports.'),
    getCourtsBySportIds: (sportIds: string[]) =>
      fromPromise(port.getCourtsBySportIds(sportIds), ERR, 'Failed to load courts.'),
    getActiveSportsCountByFacility: (facilityId: string) =>
      fromPromise(port.getActiveSportsCountByFacility(facilityId), ERR, 'Failed to load active facility sports count.'),
    getActiveCourtsCountByFacility: (facilityId: string) =>
      fromPromise(port.getActiveCourtsCountByFacility(facilityId), ERR, 'Failed to load active facility courts count.'),
    getConfirmedBookingStatsByFacility: (
      facilityId: string,
      range: { today: string; weekStart: string; weekEnd: string; monthStart: string; monthEnd: string }
    ) =>
      fromPromise(port.getConfirmedBookingStatsByFacility(facilityId, range), ERR, 'Failed to load facility booking statistics.'),
    getUserRoles: () =>
      fromPromise(port.getUserRoles(), ERR, 'Failed to load user roles.'),
    getUserRolesByUserIds: (userIds: string[]) =>
      fromPromise(port.getUserRolesByUserIds(userIds), ERR, 'Failed to load page user roles.'),
    getProfilesForAdminView: (params: { isSuperAdmin: boolean; adminFacilityIds: string[] }) =>
      fromPromise(port.getProfilesForAdminView(params), ERR, 'Failed to load admin profiles.'),
    getProfilesForAdminViewPage: (params: {
      isSuperAdmin: boolean;
      adminFacilityIds: string[];
      page: number;
      pageSize: number;
      searchTerm?: string;
    }) =>
      fromPromise(port.getProfilesForAdminViewPage(params), ERR, 'Failed to load paged admin profiles.'),
    getCourtAvailability: (courtId: string) =>
      fromPromise(port.getCourtAvailability(courtId), ERR, 'Failed to load court availability.'),
    getSpecialHours: (tableName: 'facility_peak_hours' | 'sport_peak_hours' | 'court_peak_hours', entityIdCol: string, entityId: string) =>
      fromPromise(port.getSpecialHours(tableName, entityIdCol, entityId), ERR, 'Failed to load special hours.'),
    geocodeAddress: (params: { address: string; city: string; country: string }) =>
      fromPromise(port.geocodeAddress(params), ERR, 'Failed to geocode address.'),
  };
}
